import { getBaseUrl, request } from 'src/helper/api';
import { WO_STATUS_KEYS } from 'src/views/VendorPortal/WorkOrder/constants';

export const getWorkOrders = (token?: string, signal?: AbortSignal) =>
  request(
    {
      method: 'GET',
      url: `${getBaseUrl()}api/work-orders`,
      contentType: 'json',
      token,
      signal,
    },
    'vendor/work-order/getWorkOrders',
  );

// vendor portal: work order details api
export const getWorkOrderDetails = (token?: string, workOrderId?: string, signal?: AbortSignal) =>
  request(
    {
      method: 'GET',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}`,
      contentType: 'json',
      token,
      signal,
    },
    'vendor/work-order/getWorkOrderDetails',
  );

export const getCategoryOptions = (token?: string, signal?: AbortSignal) =>
  request(
    {
      method: 'GET',
      url: `${getBaseUrl()}api/work-orders/filter?type=category`,
      contentType: 'json',
      token,
      signal,
    },
    'vendor/work-order/getCategoryOptions',
  );

export const markIssueAsDone = (workOrderId?: string, token?: string, issueId?: string) => {
  const url = `${getBaseUrl()}api/work-orders/${workOrderId}/issues/${issueId}/actions/markAsDone`;
  return request(
    {
      method: 'POST',
      url,
      contentType: 'json',
      token,
    },
    'vendor/work-order/markIssueAsDone',
  );
};

export const saveServiceReportChanges = (token?: string, workOrderId?: string, issueId?: string) =>
  request(
    {
      method: 'PUT',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/issues/${issueId}/actions/updateIssue`,
      contentType: 'json',
      token,
    },
    'vendor/work-order/saveServiceReportChanges',
  );

export const closeWorkOrder = (workOrderId?: string, token?: string) => {
  const url = `${getBaseUrl()}api/work-orders/${workOrderId}/actions/close`;
  return request(
    {
      method: 'POST',
      url,
      contentType: 'json',
      token,
      shouldAddSocketId: true,
    },
    'vendor/work-order/close',
  );
};

export const rescheduleWorkOrder = (workOrderId: string, token?: string) =>
  request(
    {
      method: 'PUT',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/actions/reschedule`,
      contentType: 'json',
      token,
    },
    'vendor/work-order/rescheduleWorkOrder',
  );

export const getVendorCancelWorkOrderOptions = (
  workOrderId?: string,
  token?: string,
  signal?: AbortSignal,
) =>
  request(
    {
      method: 'GET',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/cancel-trip-info`,
      contentType: 'json',
      token,
      signal,
    },
    'vendor/work-order/getVendorCancelWorkOrderOptions',
  );

export const cancelVendorWorkOrder = (workOrderId?: string, token?: string) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/actions/cancelTrip`,
      contentType: 'json',
      token,
    },
    'vendor/work-order/cancelVendorWorkOrder',
  );

export const addQuote = (workOrderId?: string, token?: string) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/actions/addQuote`,
      contentType: 'json',
      token,
    },
    'vendor/work-order/addQuote',
  );

export const updateQuote = (workOrderId?: string, quoteId?: string, token?: string) =>
  request(
    {
      method: 'PUT',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/quotes/${quoteId}/actions/updateQuote`,
      contentType: 'json',
      token,
    },
    'work-order/updateQuote',
  );

export const addQuoteTask = (workOrderId?: string, quoteId?: string, token?: string) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/quotes/${quoteId}/actions/addQuoteTask`,
      contentType: 'json',
      token,
    },
    'vendor/work-order/addQuoteTask',
  );

export const updateQuoteTask = (
  workOrderId?: string,
  quoteId?: string,
  quoteTaskId?: string,
  token?: string,
) =>
  request(
    {
      method: 'PUT',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/quotes/${quoteId}/quote-tasks/${quoteTaskId}/actions/updateQuoteTask`,
      contentType: 'json',
      token,
    },
    'vendor/work-order/updateQuoteTask',
  );

export const removeQuoteTask = (
  workOrderId?: string,
  quoteId?: string,
  quoteTaskId?: string,
  token?: string,
  signal?: AbortSignal,
) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/quotes/${quoteId}/quote-tasks/${quoteTaskId}/actions/removeQuoteTask`,
      contentType: 'form-data',
      token,
      signal,
    },
    'vendor/work-order/removeQuoteTask',
  );

// Same endpoint as removeQuoteTask, deliberately under a DIFFERENT action type. `removeQuoteTask`'s
// fulfilled reducer is written for issue-linked tasks: it keeps the tasks whose `work_order_issue_id`
// differs from the response's `issue_id`. An additional (no-issue) task responds without an
// `issue_id`, so that filter would discard EVERY additional task and then drop the emptied quote
// from the store. This type has no extra-reducer case at all — the caller updates the store with
// `editQuoteDetails` — which also keeps the ORI-614 issue-removal fix untouched.
export const removeAdditionalQuoteTask = (
  workOrderId?: string,
  quoteId?: string,
  quoteTaskId?: string,
  token?: string,
  signal?: AbortSignal,
) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/quotes/${quoteId}/quote-tasks/${quoteTaskId}/actions/removeQuoteTask`,
      contentType: 'form-data',
      token,
      signal,
    },
    'vendor/work-order/removeAdditionalQuoteTask',
  );

export const rescheduleVendorWorkOrder = (
  workOrderId?: string,
  token?: string,
  signal?: AbortSignal,
) => {
  const url = `${getBaseUrl()}api/work-orders/${workOrderId}/actions/reschedule`;
  return request(
    {
      method: 'PUT',
      url,
      contentType: 'json',
      token,
      signal,
    },
    'vendor/work-order/reschedule',
  );
};
export const scheduleVendorReturnTrip = (
  workOrderId?: string,
  token?: string,
  signal?: AbortSignal,
) => {
  const url = `${getBaseUrl()}api/work-orders/${workOrderId}/actions/scheduleReturnTrip`;
  return request(
    {
      method: 'PUT',
      url,
      contentType: 'json',
      token,
      signal,
    },
    'vendor/work-order/scheduleReturnTrip',
  );
};
export const markAsUnresolve = (workOrderId?: string, token?: string, issueId?: string) => {
  const url = `${getBaseUrl()}api/work-orders/${workOrderId}/issues/${issueId}/actions/decline`;
  return request(
    {
      method: 'POST',
      url,
      contentType: 'json',
      token,
    },
    'vendor/work-order/markAsUnresolve',
  );
};
export function markAsPending(workOrderId?: string, token?: string, issueId?: string) {
  const url = `${getBaseUrl()}api/work-orders/${workOrderId}/issues/${issueId}/actions/markAsPending`;
  return request(
    {
      method: 'POST',
      url,
      contentType: 'json',
      token,
    },
    'vendor/work-order/markAsPending',
  );
}
export const updateDeclineReason = (workOrderId?: string, token?: string, issueId?: string) => {
  const url = `${getBaseUrl()}api/work-orders/${workOrderId}/issues/${issueId}/actions/updateDeclineReason`;
  return request(
    {
      method: 'PATCH',
      url,
      contentType: 'json',
      token,
    },
    'vendor/work-order/updateDeclineReason',
  );
};
export const enRouteWorkOrder = (workOrderId?: string, token?: string) =>
  request(
    {
      method: 'PATCH',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/actions/enRoute`,
      contentType: 'json',
      token,
    },
    'vendor/work-order/enRouteWorkOrder',
  );
export const pauseEnRouteWorkOrder = (workOrderId?: string, token?: string) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/actions/pauseEnRoute`,
      contentType: 'json',
      token,
    },
    'vendor/work-order/pauseEnRouteWorkOrder',
  );
export const pauseWorkInTrip = (workOrderId?: string, token?: string) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/actions/pauseTimer`,
      contentType: 'json',
      token,
    },
    'vendor/work-order/pauseWorkInTrip',
  );
export const resumeEnRouteWorkOrder = (workOrderId?: string, token?: string) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/actions/resumeEnRoute`,
      contentType: 'json',
      token,
    },
    'vendor/work-order/resumeEnRouteWorkOrder',
  );
export const resumeWorkInTrip = (workOrderId?: string, token?: string) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/actions/resumeWork`,
      contentType: 'json',
      token,
    },
    'vendor/work-order/resumeWorkInTrip',
  );
export const startWorkInTrip = (workOrderId?: string, token?: string) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/actions/startWork`,
      contentType: 'json',
      token,
    },
    'vendor/work-order/startWorkInTrip',
  );

/**
 * Utility function to check if all issues are marked and open modal after action completes
 * @param resultPromise - The promise returned from dispatching an action
 * @param dispatch - Redux dispatch function
 * @param getState - Redux getState function
 */
export const checkAllIssuesMarkedThunk = (resultPromise: any, dispatch: any, getState: any) => {
  resultPromise
    .then(async (result: any) => {
      // Only open modal if the action was successful (not rejected)
      if (result.type && !result.type.endsWith('/rejected')) {
        // Import the modal action dynamically to avoid circular dependencies
        const { openAllIssuesClosedModal } = await import('src/redux/vendor-portal/modals/slice');

        // Check if all issues are now marked
        const { status } = getState().vendor['work-orders'].source;
        const { areAllIssuesMarked } = getState().vendor['work-orders'].sidePanels.details;

        if (status.value === WO_STATUS_KEYS.IN_PROGRESS && areAllIssuesMarked) {
          dispatch(openAllIssuesClosedModal());
        }
      }
    })
    .catch(() => {
      // Silently handle errors - the component will handle them via .unwrap()
    });
};
